import { HStack, Text, VStack } from "@chakra-ui/layout";
import { Skeleton, SkeletonText, type StyleProps } from "@chakra-ui/react";
import {
  ProgressBar,
  type TProgressBarSegment,
} from "@src/components/ui-kit/NewProgressBar";
import { currency, HUNDRED_PERCENT, percent } from "@src/utils/formatters";
import { useMemo } from "react";

type SummaryRowProps = {
  label: string;
  amount: number;
  percentage?: number;
  isLoading?: boolean;
  bottomMargin?: boolean;
  overHundredPercentColor?: StyleProps["bg"];
};

interface BillingProfitSummaryRowProps extends SummaryRowProps {}
export const BillingProfitSummaryRow = ({
  label,
  amount,
  percentage,
  isLoading,
  bottomMargin,
  overHundredPercentColor = "green.500",
}: BillingProfitSummaryRowProps) => {
  const showPercentage = percentage !== undefined;
  const overHundredPercent = (percentage ?? 0) - HUNDRED_PERCENT;
  const isOverHundredPercent = overHundredPercent > 0;

  return (
    <VStack
      fontSize="sm"
      mb={bottomMargin ? "3" : undefined}
      align="justify"
      spacing="0"
    >
      <HStack justify="space-between" align="start">
        <Text fontSize="inherit" color="gray.500">
          {label}
        </Text>

        <SkeletonText isLoaded={!isLoading} noOfLines={1} skeletonHeight="3">
          <Text
            fontWeight="bold"
            color={amount < 0 ? "red.500" : undefined}
            fontSize="inherit"
          >
            {currency.formatByWorkspace(amount)}
          </Text>
        </SkeletonText>
      </HStack>

      {showPercentage && (
        <HStack justify="end">
          <SkeletonText isLoaded={!isLoading} noOfLines={1} skeletonHeight="3">
            <Text
              color={isOverHundredPercent ? overHundredPercentColor : undefined}
            >
              ({percent.format(percentage ?? 0)})
            </Text>
          </SkeletonText>
        </HStack>
      )}
    </VStack>
  );
};

interface PlannedExpensesSummaryRowProps
  extends Omit<SummaryRowProps, "label"> {
  progressBarLabel?: string;
}
export const PlannedExpensesSummaryRow = ({
  progressBarLabel,
  amount,
  percentage = 0,
  isLoading,
  bottomMargin,
  overHundredPercentColor = "green.500",
}: PlannedExpensesSummaryRowProps) => {
  const segments = useMemo((): TProgressBarSegment[] => {
    const baseColor: StyleProps["bg"] = percentage < 0 ? "red.500" : "teal.400";
    const overHundredPercent = percentage - HUNDRED_PERCENT;
    const isOverHundredPercent = overHundredPercent > 0;

    if (isOverHundredPercent) {
      return [
        { percent: HUNDRED_PERCENT, bg: baseColor },
        { percent: overHundredPercent, bg: overHundredPercentColor },
      ];
    }
    return [{ percent: Math.abs(percentage), bg: baseColor }];
  }, [percentage, amount, overHundredPercentColor]);

  return (
    <VStack
      fontSize="xs"
      mb={bottomMargin ? "3" : undefined}
      align="justify"
      spacing="1"
    >
      <SkeletonText isLoaded={!isLoading} noOfLines={1} skeletonHeight="4">
        <Text
          fontWeight="bold"
          color={amount < 0 ? "red.500" : undefined}
          fontSize="lg"
        >
          {currency.formatByWorkspace(amount)}
        </Text>
      </SkeletonText>

      <HStack spacing="0" justify="space-between" align="center">
        <Text fontSize="sm" color="gray.500">
          {progressBarLabel}
        </Text>

        <SkeletonText isLoaded={!isLoading} noOfLines={1} skeletonHeight="3">
          <Text fontSize="sm" color="gray.500">
            {percent.format(percentage ?? 0)}
          </Text>
        </SkeletonText>
      </HStack>

      <Skeleton w="full" isLoaded={!isLoading} rounded="lg">
        <ProgressBar size="sm" segments={segments} />
      </Skeleton>
    </VStack>
  );
};

type AmountCellProps = {
  amount: number;
};
export const AmountCell = ({ amount }: AmountCellProps) => {
  return (
    <Text color={amount < 0 ? "red.500" : undefined}>
      {currency.formatByWorkspace(amount)}
    </Text>
  );
};
